import { useEffect, useState } from 'react';
import { apiService } from '@services/api';
import logger from '@utils/logger';

export interface ClientCatalogEntry {
  _id: string;
  id: string;
  name: string;
  address?: string;
  isActive?: boolean;
  profileImage?: string | null;
}

/**
 * Carrega a lista de condomínios do usuário logado (`scopeToAssigned: true` — só os que ele tem
 * permissão para ver, mesma regra já aplicada em `ClientFilter.tsx`). Extraído de dentro de
 * `ClientFilter` (02/09) para ser reutilizável fora do `DemandsContext` — usado por
 * `ClientFilter` (por baixo) e por `CloneFromClientDialog` (escolha do condomínio de ORIGEM do
 * clone, uma seleção local e descartável, à parte de qual condomínio está filtrado na página —
 * por isso não usa `DemandsContext.selectedClients`, que é o filtro principal/persistido).
 */
export function useClientCatalog() {
  const [clients, setClients] = useState<ClientCatalogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        // Pedido do usuário (03/09): sem `limit`, a API cai no default de paginação (10) — em
        // qualquer tenant com mais de 10 condomínios, o filtro (aqui e em ClientFilter/
        // CloneFromClientDialog, que usam este mesmo hook por baixo) simplesmente não deixava
        // escolher os demais. 100 é o teto máximo que a API aceita (`parsePaginationOptions`,
        // backend/src/utils/pagination.ts) — mesmo valor de teto que o mobile já usa
        // (`use-client-scope.ts`, `limit: 200`, também clampado a 100 no servidor).
        const response = await apiService.getClients({ isActive: true, scopeToAssigned: true, limit: 100 });
        if (cancelled) return;
        if (response.data.success) {
          const allClients = (response.data.data || []) as Array<Partial<ClientCatalogEntry> & { _id?: string }>;
          setClients(
            allClients.map((client) => ({
              ...client,
              id: String(client.id || client._id),
              _id: String(client._id || client.id),
            })) as ClientCatalogEntry[]
          );
        } else {
          setClients([]);
        }
      } catch (error) {
        if (cancelled) return;
        logger.error('Erro ao carregar catálogo de condomínios', error);
        setClients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { clients, loading };
}
